<template>
  <b-card header-class="p-0 border-0">
    <template #header>
      <b-button
        block
        variant="light"
        class="border-bottom"
      >
        <div class="text-left">
          Visualisation Type
        </div>
      </b-button>
    </template>

    <b-dropdown
      variant="outline-primary"
      menu-class="p-0 border-0 w-100"
      block
      :disabled="visualisationOptions.length <= 1"
      data-test="dropdown"
    >
      <template #button-content>
        {{ visualisationOptionText }} <BetaBadge v-if="visualisationType === SUNBURST" />
      </template>

      <b-dropdown-item link-class="p-0">
        <b-button-group
          vertical
          class="w-100"
        >
          <b-button
            v-for="vis in visualisationOptions"
            :key="`btn-vis-type-${vis.value}`"
            :variant="vis.value === visualisationType ? 'primary' : 'outline-primary'"
            :disabled="vis.disabled"
            block
            :data-test="`btn-vis-type-${vis.value}`"
            @click="onSelectType(vis.value)"
          >
            {{ vis.text }} <BetaBadge v-if="vis.value === SUNBURST" /> <HelpfulTooltip
              v-if="vis.disabled"
              :text="vis.disabledTooltipText"
            />
          </b-button>
        </b-button-group>
      </b-dropdown-item>
    </b-dropdown>
  </b-card>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import BetaBadge from '@/components/builder/UI/BetaBadge.vue'
import HelpfulTooltip from '@/components/builder/UI/HelpfulTooltip.vue'

import { VISUALISATION_CONFIG_KEY } from '@/constants'
import { VisualisationType, VisualisationTypes } from '@/types'

const components = {
  BetaBadge,
  HelpfulTooltip,
}

@Component({ components })
export default class VisualisationTypeDropdown extends Vue {
  SUNBURST = VisualisationTypes.SUNBURST

  get isSunburstDisabled () {
    // Sunburst is only supported when the data hierarchy has 3 levels
    return this.$typedStore.primary.app.levels.maxLevel !== 3
  }

  get visualisationOptions (): {text: string, value: VisualisationType, disabled: boolean, disabledTooltipText?: string}[] {
    switch (this.$typedStore.activeVisualisation.visualisationType) {
      case VisualisationTypes.NONE:
        return [{ text: 'None', value: VisualisationTypes.NONE, disabled: true }]
      case VisualisationTypes.CUSTOM:
        return [{ text: 'Custom', value: VisualisationTypes.CUSTOM, disabled: false }]
      case VisualisationTypes.BUBBLES:
      case VisualisationTypes.SUNBURST:
        return [
          { text: 'Bubbles', value: VisualisationTypes.BUBBLES, disabled: false },
          { text: 'Sunburst', value: VisualisationTypes.SUNBURST, disabled: this.isSunburstDisabled, disabledTooltipText: 'Sunburst is only supported when the data hierarchy has been defined with 3 levels' },
        ]
    }
  }

  get visualisationType () {
    return this.$typedStore.activeVisualisation.visualisationType
  }

  set visualisationType (value) {
    this.$typedStore.primary.app.updateConfig({ configPath: [VISUALISATION_CONFIG_KEY, 'type'], value })
  }

  get visualisationOption () {
    return this.visualisationOptions.find(({ value }) => value === this.visualisationType)
  }

  get visualisationOptionText () {
    return this.visualisationOption!.text
  }

  onSelectType (value: VisualisationType) {
    this.visualisationType = value
  }
}

</script>
